/**
 * Formats tiersBenefits into categories with benefits and their values across variants
 * @param tiersBenefits - Array of tier objects with benefits
 * @returns Array of category objects with benefits and their values across variants
 */
const formatTiersBenefits = (
  tiersBenefits: Array<{
    variant: string;
    benefits: Array<{
      key: string;
      name: string;
      value: string;
      categoryKey: string;
      categoryLabel: string;
    }>;
  }>,
) => {
  // Create a Map to store categories by categoryKey
  const categoriesMap = new Map<
    string,
    {
      categoryKey: string;
      categoryLabel: string;
      benefits: Map<
        string,
        {
          key: string;
          name: string;
          [variant: string]: string;
        }
      >;
    }
  >();

  // Get all variants
  const variants = tiersBenefits.map((tier) => tier.variant);

  // Get the last tier which we assume has all available benefits
  const lastTier = tiersBenefits[tiersBenefits.length - 1];

  // Create a map to track the order of benefits in each category from the last tier
  const categoryBenefitOrderMap = new Map<string, string[]>();

  // First process the last tier to establish all benefits and their order
  if (lastTier) {
    lastTier.benefits.forEach((benefit) => {
      const { key, categoryKey } = benefit;

      // Track the order of benefits within each category
      if (!categoryBenefitOrderMap.has(categoryKey)) {
        categoryBenefitOrderMap.set(categoryKey, []);
      }

      const benefitOrder = categoryBenefitOrderMap.get(categoryKey)!;
      if (!benefitOrder.includes(key)) {
        benefitOrder.push(key);
      }
    });
  }

  // Process each tier and its benefits
  tiersBenefits.forEach((tier) => {
    const { variant, benefits } = tier;

    benefits.forEach((benefit) => {
      const { key, name, value, categoryKey, categoryLabel } = benefit;

      // Get or create category
      if (!categoriesMap.has(categoryKey)) {
        categoriesMap.set(categoryKey, {
          categoryKey,
          categoryLabel,
          benefits: new Map(),
        });
      }

      const category = categoriesMap.get(categoryKey)!;

      // Get or create benefit
      if (!category.benefits.has(key)) {
        const benefitObj: { key: string; name: string } = {
          key,
          name,
        };

        // Initialize all variants with "-"
        variants.forEach((v) => {
          benefitObj[v] = '-';
        });

        category.benefits.set(key, benefitObj);
      }

      // Set the value for this variant
      const benefitObj = category.benefits.get(key)!;
      benefitObj[variant] = value;
    });
  });

  // Convert Maps to arrays, respecting the order from the last tier
  const result = Array.from(categoriesMap.values()).map((category) => {
    // Define the type for orderedBenefits to match the benefit object structure
    const orderedBenefits: Array<{
      key: string;
      name: string;
      [variant: string]: string;
    }> = [];

    const benefitOrder = categoryBenefitOrderMap.get(category.categoryKey) || [];

    // First add benefits in the order they appear in the last tier
    for (const key of benefitOrder) {
      const benefit = category.benefits.get(key);
      if (benefit) {
        orderedBenefits.push(benefit);
      }
    }

    // Then add any remaining benefits that weren't in the last tier
    category.benefits.forEach((benefit) => {
      if (!benefitOrder.includes(benefit.key)) {
        orderedBenefits.push(benefit);
      }
    });

    return {
      categoryKey: category.categoryKey,
      categoryLabel: category.categoryLabel,
      benefits: orderedBenefits,
    };
  });

  return result;
};

export default formatTiersBenefits;
